/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import Label from 'components/_utility/Label';
import Radio from 'components/_form/Radio/Radio';
import Checkbox from 'components/_form/Checkbox/Checkbox';
import request from 'helpers/request';
import { getLanguageCode, getTwoLetterIsoName } from 'helpers/utilities';
import { getLoadedConfiguration } from 'src/redux/garage/selectors';
import { modalPropsSelector, getSelectedProvince } from 'src/redux/configurator/selectors';
import CheckIcon from 'src/assets/icons/check.svg';
import Button from 'components/Button/Button';
import { getGarageEmailAddress } from 'helpers/garage';
import useLabel from 'src/hooks/useLabel';
import { getSendToDealerData, lgpdSelector, getGoalId } from 'src/redux/app/selectors';
import fetchGoal from 'helpers/goals';
import useDataLayer from 'src/hooks/useDataLayer';
import { closeModal } from 'src/redux/configurator/actions';
import {
  Wrap,
  Details,
  Locator,
  Heading,
  Description,
  FormFieldGroup,
  FormLabel,
  FormInput,
  LocationLookup,
  PostCodeInput,
  SearchButton,
  Or,
  GeoButton,
  Radios,
  Checkboxes,
  CheckboxWrapper,
  PostCodeSearchWrap,
  PostCodeLookupWrap,
  SendButton,
  ResultCard,
  ResultAddress,
  ResultDistance,
  ResultTitle,
  Results,
  InputError,
  SuccessWrap,
  SuccessCircle,
  SuccessTitle,
  SuccessDescription,
  Text,
  ChevronDown,
  ContentToggle,
  AdditionalContent,
} from './SendToDealer.styled';

const SendToDealerSchema = isLgpdActive =>
  Yup.object().shape({
    firstName: Yup.string().required('requiredLabel'),
    lastName: Yup.string().required('requiredLabel'),
    email: Yup.string()
      .email('invalidEmailLabel')
      .required('requiredLabel'),
    phone: Yup.string().required('requiredLabel'),
    dataConsent: isLgpdActive ? Yup.bool().oneOf([true], 'dataConsentRequired') : undefined,
    agreeToTerms: isLgpdActive ? Yup.bool().oneOf([true], 'agreeToTermsRequired') : undefined,
    agreeToPrivacyPolicy: isLgpdActive ? undefined : Yup.bool().oneOf([true], 'requiredLabel'),
  });

const SendToDealer = () => {
  const dispatch = useDispatch();
  const getLabel = useLabel();
  const [results, setResults] = useState([]);
  const [latLng, setLatLng] = useState(null);
  const [showLgpdContent, setShowLgpdContent] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [sendingToDealer, setSendingToDealer] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [postcodeError, setPostcodeError] = useState(false);
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const lgpd = useSelector(lgpdSelector);
  const props = useSelector(modalPropsSelector('sendToDealer'));
  const sendToDealerData = useSelector(getSendToDealerData);
  const sendConfigurationGoalId = useSelector(getGoalId('send configuration to dealer'));
  const pushToDataLayer = useDataLayer();
  const selectedProvince = useSelector(getSelectedProvince);

  useEffect(() => {
    pushToDataLayer('configuratorSendDealerOpen');
  }, []);

  const handleSearch = async coords => {
    const bikeId = (props && props?.bikeModelId) || loadedConfiguration?.bikeId;

    try {
      const res = await request('/api/v2/places/search', {
        method: 'POST',
        body: JSON.stringify({
          BikeId: bikeId,
          Input: '',
          PlaceId: '',
          Skip: 0,
          Take: 4,
          Latitude: coords.lat,
          Longitude: coords.lng,
          LanguageCode: getLanguageCode(),
          SiteLanguageCode: getLanguageCode(),
          Distance: 200,
          SearchByDistance: true,
          IsDealerRequest: true,
          IsPurchaseIntentRequest: true,
          ...(selectedProvince?.id && { ProvinceId: selectedProvince.id }),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      setLatLng(coords);
      setResults(res.data.DealerCardData.DealerCards);
    } catch (ex) {} // eslint-disable-line

    setLoadingResults(false);
  };

  const handlePostcodeLookup = async postcode => {
    setLoadingResults(true);

    try {
      const { data: coords } = await request(`/api/v2/geocode`, {
        method: 'POST',
        body: JSON.stringify({
          postalCode: postcode.trim().replace(/ /g, '+'),
          countryCode: getTwoLetterIsoName()
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!coords) {
        setLoadingResults(false);
        return setPostcodeError(true);
      }

      handleSearch(coords);
    } catch (ex) {
      setLoadingResults(false);
    }
  };

  const handleUseMyLocation = () => {
    setLoadingResults(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position =>
          handleSearch({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }),
        () => setLoadingResults(false)
      );
    }
  };

  const handleSendToDealer = async values => {
    try {
      setSendingToDealer(true);

      const configurationId = (props && props?.configurationId) || loadedConfiguration?.id;
      const bikeId = (props && props?.bikeModelId) || loadedConfiguration?.bikeId;

      const res = await request(`/api/configurator/garage/${configurationId}/sendtodealer`, {
        method: 'POST',
        body: JSON.stringify({
          ConfigurationUrl: `${window.location.origin}/configure/bike/${bikeId}/configure?configurationId=${configurationId}#config`,
          Dealer: selectedDealer,
          Email: values.email,
          FirstName: values.firstName,
          LastName: values.lastName,
          PostCode: values.postcode.trim() || 'N/A',
          Phone: values.phone,
          Latitude: latLng.lat,
          Longitude: latLng.lng,
          DataConsentOptIn: !!values.dataConsentOptIn,
          DataConsentDoNotContact: !!values.dataConsentDoNotContact,
          MarketingOptIn: values.receiveCommunication,
          AgreeToTerms: true,
          PrivacyPolicyAccepted: true,
          UserLocation: values.postcode,
          Medium: window.visitData.medium,
          Source: window.visitData.source,
          CampaignName: window.visitData.campaignName,
          CampaignCode: window.visitData.campaignCode,
          CaptureUrl: window.location.href,
          CaptureComponent: document.getElementById('configurator-root')?.getAttribute?.('data-sc-component'),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      await fetchGoal(sendConfigurationGoalId);
      pushToDataLayer('configuratorSendDealerSuccess', {
        event: 'gtm.sendToDealerSubmission',
        user_data: {
          sha_256_email_address: res?.data?.EmailAddressHash,   
          sha_256_phone_number: res?.data?.PhoneNumberHash
        },
      });
      setSendingToDealer(false);
      setSuccess(true);
    } catch (ex) {
      setError(true);
      console.error(ex);
    } // eslint-disable-line
  };

  if (success) {
    return (
      <SuccessWrap>
        <SuccessCircle>
          <CheckIcon />
        </SuccessCircle>
        <SuccessTitle>
          <Label name="sendToDealerSuccessTitle" />
        </SuccessTitle>
        <SuccessDescription>
          <Label name="sendToDealerSuccessDescription"></Label>
        </SuccessDescription>
        <Button onClick={() => dispatch(closeModal('sendToDealer'))}>
          <Label name="backLabel" />
        </Button>
      </SuccessWrap>
    );
  }

  if (error) {
    return (
      <SuccessWrap>
        <SuccessTitle>
          <Label name="genericErrorTitle" />
        </SuccessTitle>
        <Button onClick={() => [setError(false), setSendingToDealer(false)]}>
          <Label name="backLabel" />
        </Button>
      </SuccessWrap>
    );
  }

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: getGarageEmailAddress() || '',
        phone: '',
        dataConsent: false,
        dataConsentOptIn: '',
        dataConsentDoNotContact: '',
        agreeToTerms: false,
        receiveCommunication: false,
        agreeToPrivacyPolicy: false,
        postcode: '',
      }}
      validationSchema={SendToDealerSchema(!!lgpd)}
      onSubmit={handleSendToDealer}
    >
      {({ values, errors, touched, handleChange, handleBlur, setValues }) => (
        <Form>
          <Wrap>
            <Details>
              <Heading>
                <Label name="sendToDealerLabel" />
              </Heading>
              <Description>
                <Label name="sendToDealerDescription" />
              </Description>
              <Heading>
                <Label name="personalInfoLabel" />
              </Heading>
              <FormFieldGroup>
                <FormLabel>
                  <Label name="firstNameLabel" />
                  <span>*</span>
                </FormLabel>
                <FormInput name="firstName" onChange={handleChange} onBlur={handleBlur} />
                {errors.firstName && touched.firstName ? (
                  <InputError>
                    <Label name={errors.firstName} />
                  </InputError>
                ) : null}
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>
                  <Label name="lastNameLabel" />
                  <span>*</span>
                </FormLabel>
                <FormInput name="lastName" onChange={handleChange} onBlur={handleBlur} />
                {errors.lastName && touched.lastName ? (
                  <InputError>
                    <Label name={errors.lastName} />
                  </InputError>
                ) : null}
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>
                  <Label name="emailLabel" />
                  <span>*</span>
                </FormLabel>
                <FormInput name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} />
                {errors.email && touched.email ? (
                  <InputError>
                    <Label name={errors.email} />
                  </InputError>
                ) : null}
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>
                  <Label name="phoneLabel" />
                  <span>*</span>
                </FormLabel>
                <FormInput name="phone" onChange={handleChange} onBlur={handleBlur} />
                {errors.phone && touched.phone ? (
                  <InputError>
                    <Label name={errors.phone} />
                  </InputError>
                ) : null}
              </FormFieldGroup>
            </Details>
            <Locator>
              <Heading>
                <Label name="findNearestDealerLabel"></Label>
              </Heading>
              <PostCodeLookupWrap>
                <FormLabel>
                  <Label name="postcodeLabel" />
                  <span>*</span>
                </FormLabel>
                <LocationLookup>
                  <PostCodeSearchWrap>
                    <PostCodeInput
                      name="postcode"
                      onChange={e => {
                        setPostcodeError(false);
                        handleChange(e);
                      }}
                    />
                    <SearchButton
                      type="button"
                      primary
                      large
                      onClick={() => handlePostcodeLookup(values.postcode)}
                      disabled={loadingResults || !values.postcode.trim()}
                    >
                      <Label name="searchLabel" />
                    </SearchButton>
                  </PostCodeSearchWrap>
                  <Or>
                    <Label name="orLabel" />
                  </Or>
                  <GeoButton type="button" large onClick={handleUseMyLocation} disabled={loadingResults}>
                    <Label name="useMyLocationLabel" />
                  </GeoButton>
                </LocationLookup>
                {postcodeError ? (
                  <InputError>
                    <Label name="invalidPostcodeLabel" />
                  </InputError>
                ) : null}
              </PostCodeLookupWrap>
              {/* <ChooseLocationWrap>
                <Label name="in" />
                <CountrySelectorWrap>
                  <CountrySelector>
                    <option>United Kingdom</option>
                  </CountrySelector>
                  <ChevronDown />
                </CountrySelectorWrap>
              </ChooseLocationWrap> */}
              {results.length ? (
                <Results>
                  {results.map(result => (
                    <ResultCard
                      selected={selectedDealer === result.DealerId}
                      onClick={() => setSelectedDealer(result.DealerId)}
                    >
                      <ResultTitle>{result.Title}</ResultTitle>
                      {result.AS400MultilineAddress && (
                        <ResultAddress dangerouslySetInnerHTML={{ __html: result.AS400MultilineAddress }} />
                      )}
                      {!result.AS400MultilineAddress && <ResultAddress>{result.AddressLine1}</ResultAddress>}
                      <ResultDistance>
                        {result.Distance} {result.DistanceMeasurement}
                      </ResultDistance>
                    </ResultCard>
                  ))}
                </Results>
              ) : null}
              {lgpd ? (
                <>
                  <Text>
                    <Label name="dataConsentLabel"></Label>
                  </Text>
                  <ContentToggle
                    role="button"
                    onClick={() => setShowLgpdContent(!showLgpdContent)}
                    isActive={showLgpdContent}
                  >
                    <Label name="moreContentToggleLabel"></Label>
                    <ChevronDown />
                  </ContentToggle>
                  <AdditionalContent isActive={showLgpdContent}>
                    <Label name="dataConsentAdditionalContentLabel"></Label>
                  </AdditionalContent>
                  <Radios>
                    <Radio
                      id="dataConsentOptIn"
                      label="dataConsentOptInLabel"
                      checked={values.dataConsentOptIn}
                      value="dataConsentOptIn"
                      name="dataConsent"
                      onChange={() =>
                        setValues(prevValues => ({
                          ...prevValues,
                          dataConsent: true,
                          dataConsentOptIn: true,
                          dataConsentDoNotContact: false,
                        }))
                      }
                    />
                    <Radio
                      id="dataConsentDoNotContact"
                      label="dataConsentDoNotContactLabel"
                      checked={values.dataConsentDoNotContact}
                      value="dataConsentDoNotContact"
                      name="dataConsent"
                      onChange={() =>
                        setValues(prevValues => ({
                          ...prevValues,
                          dataConsent: true,
                          dataConsentOptIn: false,
                          dataConsentDoNotContact: true,
                        }))
                      }
                    />
                    {touched.dataConsent && errors.dataConsent && (
                      <InputError>
                        <Label name={errors.dataConsent}></Label>
                      </InputError>
                    )}
                  </Radios>
                  <Checkboxes>
                    {values.dataConsentOptIn && (
                      <CheckboxWrapper>
                        <Checkbox
                          id="receiveCommunication"
                          name="receiveCommunication"
                          checked={values.receiveCommunication}
                          onChange={handleChange}
                        >
                          <Label name="receiveCommunicationConsentLabel"></Label>
                        </Checkbox>
                      </CheckboxWrapper>
                    )}
                    <CheckboxWrapper>
                      <Checkbox
                        id="agreeToTerms"
                        name="agreeToTerms"
                        checked={values.agreeToTerms}
                        onChange={handleChange}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: getLabel(
                              'agreeToTermsLabel',
                              'I have read and understand the Triumph {0}, {1}, and {2}'
                            )
                              .replace(
                                '{0}',
                                `<a href="${lgpd.privacyPolicyLink}" target="_blank">${getLabel(
                                  'privacyPolicyLinkLabel'
                                )}</a>`
                              )
                              .replace(
                                '{1}',
                                `<a href="${lgpd.cookiePolicyLink}" target="_blank">${getLabel(
                                  'cookiePolicyLinkLabel'
                                )}</a>`
                              )
                              .replace(
                                '{2}',
                                `<a href="${lgpd.termsAndConditionsLink}" target="_blank">${getLabel(
                                  'termsAndConditionsLinkLabel'
                                )}</a>`
                              ),
                          }}
                        />
                      </Checkbox>
                      {touched.agreeToTerms && errors.agreeToTerms && (
                        <InputError>
                          <Label name="agreeToTermsRequiredLGPD"></Label>
                        </InputError>
                      )}
                    </CheckboxWrapper>
                  </Checkboxes>
                </>
              ) : (
                <Checkboxes>
                  <CheckboxWrapper>
                    <Checkbox
                      id="receiveCommunication"
                      name="receiveCommunication"
                      checked={values.receiveCommunication}
                      onChange={handleChange}
                    >
                      <Label name="receiveCommunicationsLabel" />
                    </Checkbox>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <Checkbox
                      id="agreeToPrivacyPolicy"
                      name="agreeToPrivacyPolicy"
                      checked={values.agreeToPrivacyPolicy}
                      onChange={handleChange}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getLabel('privacyPolicyDescription', 'agree to {0}').replace(
                            '{0}',
                            `<a href="${sendToDealerData.privacyPolicy}" target="_blank">${getLabel(
                              'privacyPolicyLabel'
                            )}</a>`
                          ),
                        }}
                      />
                    </Checkbox>
                    {errors.agreeToPrivacyPolicy && touched.agreeToPrivacyPolicy ? (
                      <InputError>
                        <Label name="agreeToTermsRequired" />
                      </InputError>
                    ) : null}
                  </CheckboxWrapper>
                </Checkboxes>
              )}
              <SendButton
                type="submit"
                large
                primary
                disabled={!selectedDealer || !values.firstName || !values.lastName || !values.email || sendingToDealer}
              >
                <Label name={sendingToDealer ? 'loadingLabel' : 'sendToDealerLabel'} />
              </SendButton>
            </Locator>
          </Wrap>
        </Form>
      )}
    </Formik>
  );
};

export default SendToDealer;
